/**
 * Password Change Validation Schemas
 *
 * Zod schemas for password change form validation.
 *
 * Created: 2025-11-14
 * Updated: 2025-11-14 - Added diacritics validation
 * Pattern: Following ProfilePage validation pattern
 */

import { z } from 'zod';
import { passwordSchema } from '@/shared/validation/common.schemas';

/**
 * Romanian diacritics not allowed in passwords
 * Includes both lowercase and uppercase variants
 */
const DIACRITICS = ['ă', 'â', 'î', 'ș', 'ț', 'Ă', 'Â', 'Î', 'Ș', 'Ț'] as const;

/**
 * Password without diacritics schema
 * Extends base passwordSchema with diacritics validation
 */
const passwordWithoutDiacriticsSchema = passwordSchema.refine(
  (password) => !DIACRITICS.some((char) => password.includes(char)),
  {
    message: 'Parola nu poate să conțină diacritice (ă, â, î, ș, ț)',
  }
);

/**
 * Password Change Form Data Type
 */
export interface PasswordChangeFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Password Change Validation Schema
 *
 * Rules:
 * - Current password: Required
 * - New password: Min 6 characters, max 100 characters, no Romanian diacritics
 * - Confirm password: Must match new password
 */
export const passwordChangeSchema = z
  .object({
    currentPassword: z.string().min(1, 'Parola actuală este obligatorie'),
    newPassword: passwordWithoutDiacriticsSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Parolele nu se potrivesc',
    path: ['confirmPassword'],
  });
